import { CaretDownSolid } from "@/icons";
import { Analytics } from "@/types";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import cn from "classnames";
import { FC } from "react";
import { Button } from "../Button";
import { getDataLinkAttributes } from "@/helpers";
import { Input } from "../Input";

type Filter =
  | {
      type: "intermediate";
      name: string;
      children: Filter[];
    }
  | {
      type: "leaf";
      name: string;
    };

type FilterProps = Filter & {
  path: string;
  rootFilterName: string;
  analytics: Analytics;
  selectAll: { text: string } & Analytics;
  deselectAll: { text: string } & Analytics;
  selectedFilters: string[];
  onSelectFilters: (paths: string[]) => void;
  onDeselectFilters: (paths: string[]) => void;
};
export const Filter: FC<FilterProps> = (filter) => {
  const leafChildren =
    filter.type === "intermediate"
      ? filter.children
          .filter((child) => child.type === "leaf")
          .map(({ name }) => `${filter.path}\\${name}`)
      : [];

  return filter.type === "intermediate" ? (
    <Disclosure data-automation-group="filter-category">
      {({ open }) => (
        <div className="flex flex-col gap-y-3">
          <DisclosureButton
            className="text-link-s text-tab-link hover:text-tab-link-hover active:text-tab-link-pressed flex flex-row items-center gap-x-2.5 px-1 py-0.5 text-left font-medium hover:bg-white hover:underline"
            {...getDataLinkAttributes({
              dld: `${filter.analytics.dld}: ${filter.rootFilterName} | ${filter.name}`,
              dlt: filter.analytics.dlt,
            })}
          >
            <CaretDownSolid
              width="16"
              height="16"
              className={cn("inline", open && "rotate-180")}
              aria-hidden
            />
            {filter.name}
          </DisclosureButton>
          <DisclosurePanel className="flex flex-col gap-y-3 pl-5.5">
            <div className="flex flex-col gap-y-3">
              {leafChildren.length > 0 && (
                <div className="flex flex-row gap-x-1">
                  <Button
                    size="xs"
                    variant="tertiary"
                    icon="none"
                    className="text-left capitalize underline hover:no-underline!"
                    onClick={() => filter.onSelectFilters(leafChildren)}
                    data-automation-id="select-all-btn-format-tab"
                    {...getDataLinkAttributes({
                      dld: filter.selectAll.dld,
                      dlt: filter.selectAll.dlt,
                    })}
                  >
                    {filter.selectAll.text}
                  </Button>
                  /
                  <Button
                    size="xs"
                    variant="tertiary"
                    icon="none"
                    className="text-left capitalize underline hover:no-underline!"
                    onClick={() => filter.onDeselectFilters(leafChildren)}
                    data-automation-id="deselect-all-btn-format-tab"
                    {...getDataLinkAttributes({
                      dld: filter.deselectAll.dld,
                      dlt: filter.deselectAll.dlt,
                    })}
                  >
                    {filter.deselectAll.text}
                  </Button>
                </div>
              )}
              {filter.children.map((nestedFilter) => (
                <Filter
                  key={`${nestedFilter.name}-${nestedFilter.type}`}
                  {...nestedFilter}
                  // pass from the top
                  rootFilterName={filter.rootFilterName}
                  analytics={filter.analytics}
                  onSelectFilters={filter.onSelectFilters}
                  onDeselectFilters={filter.onDeselectFilters}
                  selectedFilters={filter.selectedFilters}
                  selectAll={filter.selectAll}
                  deselectAll={filter.deselectAll}
                  path={`${filter.path + "\\" + nestedFilter.name}`}
                />
              ))}
            </div>
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  ) : (
    <Input
      label={filter.name}
      checked={filter.selectedFilters.includes(filter.path)}
      type="checkbox"
      variant="filter"
      {...getDataLinkAttributes({
        dlt: filter.analytics.dlt,
        dld: `${filter.analytics.dld}: ${filter.rootFilterName} | ${filter.name}`,
      })}
      onChange={(checked) => {
        if (checked) {
          filter.onSelectFilters([filter.path]);
        } else {
          filter.onDeselectFilters([filter.path]);
        }
      }}
    />
  );
};
